/**
 * Index → sidebar tree. The first title segment is a section (drawn as a mono
 * label), middle segments are folders, the last is a component holding its
 * docs page and stories. A component whose only entry is a docs page is a
 * page and links straight to it.
 */
export function buildTree(index) {
  const root = { type: 'root', children: [], byKey: new Map() };
  for (const id of index.order) {
    const entry = index.entries[id];
    const segments = entry.title.split('/').map((s) => s.trim()).filter(Boolean);
    let parent = root;
    let pathKey = '';
    segments.forEach((seg, i) => {
      pathKey += '/' + seg;
      const isLeaf = i === segments.length - 1;
      let node = parent.byKey.get(seg);
      if (!node) {
        node = {
          type: isLeaf ? 'component' : i === 0 ? 'section' : 'folder',
          name: seg,
          key: pathKey,
          children: [],
          byKey: new Map(),
          entries: [],
        };
        if (isLeaf && segments.length === 1) node.type = 'component';
        parent.byKey.set(seg, node);
        parent.children.push(node);
      } else if (isLeaf && node.type !== 'component' && node.entries.length === 0) {
        // A title that is also a folder (rare): keep both roles.
        node.entries = node.entries ?? [];
      }
      if (isLeaf) node.entries.push(entry);
      parent = node;
    });
  }
  // Top-level components (single-segment titles) are gathered into an
  // untitled section so the sidebar keeps one rhythm.
  const loose = root.children.filter((n) => n.type === 'component');
  if (loose.length) {
    const section = { type: 'section', name: '', key: '/_', children: loose, byKey: new Map(), entries: [] };
    root.children = [section, ...root.children.filter((n) => n.type !== 'component')];
  }
  return root;
}

export function firstEntryId(node) {
  if (node.entries?.length) {
    const docs = node.entries.find((e) => e.type === 'docs');
    return (docs ?? node.entries[0]).id;
  }
  for (const child of node.children ?? []) {
    const id = firstEntryId(child);
    if (id) return id;
  }
  return null;
}

/** Path keys of every ancestor of the entry, so the sidebar can open them. */
export function ancestorsOf(entry) {
  if (!entry) return [];
  const segments = entry.title.split('/').map((s) => s.trim());
  const out = [];
  let key = '';
  for (const seg of segments) {
    key += '/' + seg;
    out.push(key);
  }
  return out;
}

/** Fuzzy-ish search over titles and story names; contiguous matches rank first. */
export function search(index, query, limit = 40) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const words = q.split(/\s+/);
  const scored = [];
  for (const id of index.order) {
    const e = index.entries[id];
    const hay = `${e.title} ${e.type === 'story' ? e.name : ''}`.toLowerCase();
    if (!words.every((w) => hay.includes(w))) continue;
    const last = e.title.split('/').at(-1).toLowerCase();
    let score = 0;
    if (last.startsWith(q)) score += 100;
    if (last.includes(q)) score += 40;
    if (e.type === 'docs') score += 15;
    if (e.type === 'story' && e.name.toLowerCase().includes(q)) score += 10;
    score -= e.title.length * 0.1;
    scored.push({ e, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.e);
}

export const normalizeQuery = (q) => q.trim().toLowerCase().replace(/\s+/g, ' ');

/** Whether a search is worth a model call (config `ask`): a phrase, not a name being typed. */
export function wantsMeaning(query, local) {
  const q = normalizeQuery(query);
  if (q.length < 8 || q.split(' ').length < 2) return false;
  return !local.some((e) => e.title.split('/').at(-1).trim().toLowerCase().includes(q));
}
